import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ListSlotOverridesQuery,
  SlotOverride,
  SlotOverrideCreateInput,
  SlotOverrideListResponse,
  SlotOverrideUpdateInput,
} from '@news-cms/shared';
import { ApiError, api } from './api';
import { toast } from '@/components/ui/toaster';

function qs(filter: Partial<ListSlotOverridesQuery>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return params.toString();
}

/* ---------- query options ---------- */

export const slotOverridesListQueryOptions = (filter: Partial<ListSlotOverridesQuery> = {}) =>
  queryOptions({
    queryKey: ['slot-overrides', 'list', filter] as const,
    queryFn: () => api<SlotOverrideListResponse>(`/api/admin/slot-overrides?${qs(filter)}`),
  });

export const slotOverrideDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['slot-overrides', 'detail', id] as const,
    queryFn: () =>
      api<{ override: SlotOverride }>(`/api/admin/slot-overrides/${id}`).then((d) => d.override),
  });

/* ---------- mutations ---------- */

function invalidate(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['slot-overrides'] });
  // Public pages may serve a different `slot.item` now — bust the homepage
  // preview cache so /homepage live render picks up the override.
  void qc.invalidateQueries({ queryKey: ['homepage'] });
  void qc.invalidateQueries({ queryKey: ['pages'] });
}

export function useCreateSlotOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SlotOverrideCreateInput) => {
      const data = await api<{ override: SlotOverride }>('/api/admin/slot-overrides', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return data.override;
    },
    onSuccess: (override) => {
      qc.setQueryData(slotOverrideDetailQueryOptions(override.id).queryKey, override);
      invalidate(qc);
      toast.success('สร้าง PR เรียบร้อย');
    },
    onError: (e) => toast.error(slotOverrideErrorMessage(e)),
  });
}

export function useUpdateSlotOverride(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SlotOverrideUpdateInput) => {
      const data = await api<{ override: SlotOverride }>(`/api/admin/slot-overrides/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      return data.override;
    },
    onSuccess: (override) => {
      qc.setQueryData(slotOverrideDetailQueryOptions(override.id).queryKey, override);
      invalidate(qc);
      toast.success('บันทึก PR เรียบร้อย');
    },
    onError: (e) => toast.error(slotOverrideErrorMessage(e)),
  });
}

export function useDeleteSlotOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ deleted: true }>(`/api/admin/slot-overrides/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: ['slot-overrides', 'detail', id] });
      invalidate(qc);
      toast.success('ลบ PR เรียบร้อย');
    },
    onError: (e) => toast.error(slotOverrideErrorMessage(e)),
  });
}

/* ---------- error messages ---------- */

export function slotOverrideErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  switch (e.code) {
    case 'VALIDATION_ERROR': {
      const details = e.details as { issues?: unknown } | undefined;
      const rawIssues = details?.issues;
      const issues =
        Array.isArray(rawIssues) && rawIssues.length > 0
          ? (rawIssues as Array<{ path: unknown[]; message: string }>)
          : null;
      if (!issues || issues.length === 0) return 'ข้อมูลไม่ถูกต้อง';
      const first = issues[0]!;
      const path = Array.isArray(first.path) ? first.path.join('.') : '';
      const more = issues.length > 1 ? ` (+${issues.length - 1} เพิ่มเติม)` : '';
      return `ข้อมูลไม่ถูกต้อง: ${path ? `${path} — ` : ''}${first.message}${more}`;
    }
    case 'OVERLAP_CONFLICT':
      return 'ช่วงเวลาทับกับ PR อื่นใน slot เดียวกัน — แก้ช่วงเวลาก่อนบันทึก';
    case 'NOT_FOUND':
      return e.message || 'ไม่พบ PR / Page / Slot / Post ตามที่อ้าง';
    case 'FORBIDDEN':
      return 'ไม่มีสิทธิ์ — ต้องมี promos.manage';
    default:
      return e.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  }
}
